import type { MealRepository, StoredMealDay } from "../application/meal-repository.js";
import { MealTransferError } from "../application/meal-transfer";
import { isMealNutritionSaveable } from "../application/nutrition-completeness";
import type { NutritionRowEstimate, ParsedVoiceFood, ParsedVoiceMealDraft } from "../application/remote-meal-parser.js";
import type { ISODate, Meal, MealItem, MealType, SyncOperation } from "../domain/model.js";

const DATABASE_NAME = "yumnote-web";
const DATABASE_VERSION = 3;
const MEAL_STORE = "meals";
const ITEM_STORE = "meal_items";
const OUTBOX_STORE = "sync_operations";

type StoredMeal = Meal & { sourceText: string };
type StoredMealItem = MealItem & { date: ISODate; parsedFood?: ParsedVoiceFood; nutritionRow?: NutritionRowEstimate };

function createId(): string {
  return typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now()}-${crypto.getRandomValues(new Uint32Array(4)).join("-")}`;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction was aborted"));
  });
}

export class IndexedDbMealRepository implements MealRepository {
  private databasePromise: Promise<IDBDatabase> | null = null;
  private readonly listeners = new Map<ISODate, Set<() => void>>();

  async getDay(date: ISODate): Promise<StoredMealDay> {
    const database = await this.open();
    const transaction = database.transaction([MEAL_STORE, ITEM_STORE], "readonly");
    const mealsRequest = transaction.objectStore(MEAL_STORE).index("date").getAll(date) as IDBRequest<StoredMeal[]>;
    const itemsRequest = transaction.objectStore(ITEM_STORE).index("date").getAll(date) as IDBRequest<StoredMealItem[]>;
    const [meals, items] = await Promise.all([requestResult(mealsRequest), requestResult(itemsRequest)]);
    await transactionDone(transaction);
    return { meals: meals.filter(meal => !meal.deletedAt), items: items.filter(item => !item.deletedAt) };
  }

  async getDraft(date: ISODate, mealType: MealType): Promise<ParsedVoiceMealDraft | null> {
    const day = await this.getDay(date);
    const meals = day.meals.filter(meal => meal.type === mealType);
    if (!meals.length) return null;
    const mealIds = new Set(meals.map(meal => meal.id));
    const items = day.items.filter(item => mealIds.has(item.mealId)) as StoredMealItem[];
    const sourceText = meals.map(meal => (meal as StoredMeal).sourceText).filter(Boolean).join(". ");
    return {
      mealType,
      sourceText,
      clarifications: [],
      items: items.map(item => item.parsedFood ?? ({ name: item.displayName, amount: item.amount, unit: item.unit === "g" ? "g" : "portion", sourceText: item.displayName, components: [] })),
      nutrition: {
        rows: items.map(item => item.nutritionRow ?? ({
          name: item.displayName, portion: `${item.amount} ${item.unit}`, calories: item.calories,
          protein: item.protein, fat: item.fat, carbs: item.carbs, valueSource: "stated",
          components: [{ name: item.displayName, amount: item.amount, unit: item.unit, grams: item.unit === "g" ? item.amount : null, calories: item.calories, protein: item.protein, fat: item.fat, carbs: item.carbs, valueSource: "stated" }],
        })),
        totalCalories: items.reduce((sum, item) => sum + item.calories, 0),
        totalProtein: items.reduce((sum, item) => sum + item.protein, 0),
        totalFat: items.reduce((sum, item) => sum + item.fat, 0),
        totalCarbs: items.reduce((sum, item) => sum + item.carbs, 0),
      },
    };
  }

  async saveDraft(date: ISODate, draft: ParsedVoiceMealDraft): Promise<void> {
    return this.persistDraft(date, draft, false);
  }

  async replaceMealType(date: ISODate, draft: ParsedVoiceMealDraft): Promise<void> {
    return this.persistDraft(date, draft, true);
  }

  async transferMeal(sourceDate: ISODate, sourceType: MealType, targetDate: ISODate, targetType: MealType, mode: "move" | "copy"): Promise<void> {
    if (sourceDate === targetDate && sourceType === targetType) {
      throw new MealTransferError("Это уже тот же день и приём пищи. Выберите другой день или приём.");
    }
    const database = await this.open();
    const transaction = database.transaction([MEAL_STORE, ITEM_STORE, OUTBOX_STORE], "readwrite");
    const done = transactionDone(transaction);
    try {
      const mealsRequest = transaction.objectStore(MEAL_STORE).index("date").getAll(sourceDate) as IDBRequest<StoredMeal[]>;
      const itemsRequest = transaction.objectStore(ITEM_STORE).index("date").getAll(sourceDate) as IDBRequest<StoredMealItem[]>;
      const [meals, items] = await Promise.all([requestResult(mealsRequest), requestResult(itemsRequest)]);
      const sourceMeals = meals.filter(meal => meal.type === sourceType && !meal.deletedAt);
      const ids = new Map(sourceMeals.map(meal => [meal.id, mode === "copy" ? createId() : meal.id]));
      const sourceItems = items.filter(item => ids.has(item.mealId) && !item.deletedAt);
      if (!sourceItems.length) throw new MealTransferError("В этом приёме пищи пока нет еды для переноса или копирования.");
      const now = new Date().toISOString();
      const movedMeals = sourceMeals.map(meal => ({ ...meal, id: ids.get(meal.id)!, date: targetDate, type: targetType, updatedAt: now, createdAt: mode === "copy" ? now : meal.createdAt, syncStatus: "pending" as const }));
      const movedItems = sourceItems.map(item => ({ ...item, id: mode === "copy" ? createId() : item.id, mealId: ids.get(item.mealId)!, date: targetDate, updatedAt: now, createdAt: mode === "copy" ? now : item.createdAt, syncStatus: "pending" as const }));
      // Relocate the stored snapshots, including settings and unknown nutrition.
      // Destination records are never replaced; both dates and the outbox commit together.
      for (const entity of [...movedMeals, ...movedItems]) {
        const isItem = "mealId" in entity;
        transaction.objectStore(isItem ? ITEM_STORE : MEAL_STORE).put(entity);
        transaction.objectStore(OUTBOX_STORE).put({ id: createId(), entityType: isItem ? "mealItem" : "meal", entityId: entity.id, mutation: "upsert", payload: JSON.stringify(entity), createdAt: now, attemptCount: 0, nextAttemptAt: now, lastError: null } satisfies SyncOperation);
      }
      await done;
    } catch (error) {
      try { transaction.abort(); } catch { /* Already aborted or completed. */ }
      await done.catch(() => {});
      throw error;
    }
    for (const date of new Set([sourceDate, targetDate])) this.listeners.get(date)?.forEach(listener => {
      try { listener(); } catch (error) { console.error("meal_transfer_listener_failed", error); }
    });
  }

  private async persistDraft(date: ISODate, draft: ParsedVoiceMealDraft, replace: boolean): Promise<void> {
    if (!draft.nutrition || !isMealNutritionSaveable(draft)) throw new Error("nutrition_incomplete");
    const database = await this.open();
    const now = new Date().toISOString();
    const meal: StoredMeal = { id: createId(), date, type: draft.mealType, sourceText: draft.sourceText, createdAt: now, updatedAt: now, deletedAt: null, syncStatus: "pending" };
    const items: StoredMealItem[] = draft.nutrition.rows.map((row, index) => ({
      id: createId(), mealId: meal.id, foodId: null, date, displayName: row.name,
      amount: row.components.reduce((sum, component) => sum + (component.grams ?? 0), 0) || 1,
      unit: row.components.some(component => component.grams !== null) ? "g" : "portion",
      calories: row.calories ?? 0, protein: row.protein, fat: row.fat, carbs: row.carbs,
      ...(row.components.some(c => c.ignored && c.calories === null) ? { nutritionIncomplete: true } : {}),
      ...(draft.items[index] ? { parsedFood: draft.items[index] } : {}), nutritionRow: row,
      createdAt: now, updatedAt: now, deletedAt: null, syncStatus: "pending",
    }));
    const operations: SyncOperation[] = [meal, ...items].map(entity => ({
      id: createId(), entityType: entity === meal ? "meal" : "mealItem", entityId: entity.id,
      mutation: "upsert", payload: JSON.stringify(entity), createdAt: now, attemptCount: 0, nextAttemptAt: now, lastError: null,
    }));
    const transaction = database.transaction([MEAL_STORE, ITEM_STORE, OUTBOX_STORE], "readwrite");
    if (replace) {
      const existingMealsRequest = transaction.objectStore(MEAL_STORE).index("date").getAll(date) as IDBRequest<StoredMeal[]>;
      const existingItemsRequest = transaction.objectStore(ITEM_STORE).index("date").getAll(date) as IDBRequest<StoredMealItem[]>;
      const [existingMeals, existingItems] = await Promise.all([requestResult(existingMealsRequest), requestResult(existingItemsRequest)]);
      const replacedMeals = existingMeals.filter(existing => existing.type === draft.mealType && !existing.deletedAt);
      const replacedIds = new Set(replacedMeals.map(existing => existing.id));
      for (const existing of [...replacedMeals, ...existingItems.filter(item => replacedIds.has(item.mealId))]) {
        const deleted = { ...existing, deletedAt: now, updatedAt: now, syncStatus: "pending" as const };
        transaction.objectStore("mealId" in deleted ? ITEM_STORE : MEAL_STORE).put(deleted);
        transaction.objectStore(OUTBOX_STORE).put({ id: createId(), entityType: "mealId" in deleted ? "mealItem" : "meal", entityId: deleted.id, mutation: "delete", payload: JSON.stringify(deleted), createdAt: now, attemptCount: 0, nextAttemptAt: now, lastError: null } satisfies SyncOperation);
      }
    }
    transaction.objectStore(MEAL_STORE).put(meal);
    for (const item of items) transaction.objectStore(ITEM_STORE).put(item);
    for (const operation of operations) transaction.objectStore(OUTBOX_STORE).put(operation);
    await transactionDone(transaction);
    this.listeners.get(date)?.forEach(listener => listener());
  }

  subscribe(date: ISODate, listener: () => void): () => void {
    const listeners = this.listeners.get(date) ?? new Set<() => void>();
    listeners.add(listener);
    this.listeners.set(date, listeners);
    return () => { listeners.delete(listener); if (!listeners.size) this.listeners.delete(date); };
  }

  private open(): Promise<IDBDatabase> {
    if (!this.databasePromise) this.databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains("water_entries")) {
          const water = database.createObjectStore("water_entries", { keyPath: "id" });
          water.createIndex("date", "date", { unique: false });
        }
        if (!database.objectStoreNames.contains(OUTBOX_STORE)) database.createObjectStore(OUTBOX_STORE, { keyPath: "id" });
        if (!database.objectStoreNames.contains(MEAL_STORE)) {
          const meals = database.createObjectStore(MEAL_STORE, { keyPath: "id" });
          meals.createIndex("date", "date", { unique: false });
        }
        if (!database.objectStoreNames.contains(ITEM_STORE)) {
          const items = database.createObjectStore(ITEM_STORE, { keyPath: "id" });
          items.createIndex("date", "date", { unique: false });
          items.createIndex("mealId", "mealId", { unique: false });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Could not open IndexedDB"));
      request.onblocked = () => reject(new Error("IndexedDB upgrade is blocked by another tab"));
    });
    return this.databasePromise;
  }
}

export const webMealRepository = new IndexedDbMealRepository();
